import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Modal, Select, Spin, Tooltip, message } from 'antd';
import { DeleteOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useGetPIIKeys, useUpdatePIIKeys } from '../../hooks/useCompanies';
import useLogout from '../../hooks/useLogout';

const emptyRow = () => ({ key: '', values: [] });

const parsePIIKeyEntries = payload => {
    if (!Array.isArray(payload) || payload.length === 0) {
        return [emptyRow()];
    }
    if (typeof payload[0] === 'string') {
        return payload.map(key => ({ key, values: [] }));
    }
    return payload.map(item => ({
        key: item?.key || '',
        values: Array.isArray(item?.values) ? item.values : [],
    }));
};

const serializeRows = rows =>
    JSON.stringify(
        (rows || [])
            .map(row => ({
                key: (row.key || '').trim(),
                values: Array.from(
                    new Set((row.values || []).map(value => String(value).trim()).filter(Boolean))
                ),
            }))
            .filter(row => row.key || row.values.length)
    );

const ManagePIIKeysModal = ({ open, companyId, companyName, onClose }) => {
    const { logout } = useLogout();
    const [piiKeyRows, setPiiKeyRows] = useState([emptyRow()]);
    const [savedSnapshot, setSavedSnapshot] = useState('[]');
    const valuesDraftRef = useRef({});

    const onFetchSuccess = response => {
        const rows = parsePIIKeyEntries(response?.data);
        setPiiKeyRows(rows);
        setSavedSnapshot(serializeRows(rows));
    };

    const onFetchError = error => {
        if (error?.response?.status === 401) {
            logout();
            return;
        }
        message.error(error?.response?.data?.message || 'Failed to load PII keys');
    };

    const { isLoading, refetch } = useGetPIIKeys(onFetchSuccess, onFetchError, companyId, open);

    useEffect(() => {
        if (open && companyId) {
            refetch();
        }
    }, [open, companyId, refetch]);

    const onSaveSuccess = () => {
        message.success('PII keys saved successfully');
        onClose();
    };

    const onSaveError = error => {
        if (error?.response?.status === 401) {
            logout();
            return;
        }
        message.error(error?.response?.data?.message || 'Failed to save PII keys');
    };

    const { mutate: updatePIIKeys, isLoading: isSaving } = useUpdatePIIKeys(onSaveSuccess, onSaveError);

    const handleKeyChange = (value, index) => {
        const nextRows = [...piiKeyRows];
        nextRows[index] = { ...nextRows[index], key: value };
        setPiiKeyRows(nextRows);
    };

    const handleValuesChange = (values, index) => {
        const previous = piiKeyRows[index].values || [];
        const draft = String(valuesDraftRef.current[index] || '').trim();
        let next = Array.isArray(values) ? values : [];

        // Ant Design tags mode toggles a chip off if you Enter the same text again.
        if (draft && previous.includes(draft) && !next.includes(draft)) {
            next = previous;
        }

        const unique = [];
        const seen = new Set();
        next.forEach(value => {
            const trimmed = String(value).trim();
            if (!trimmed || seen.has(trimmed)) {
                return;
            }
            seen.add(trimmed);
            unique.push(trimmed);
        });

        const nextRows = [...piiKeyRows];
        nextRows[index] = { ...nextRows[index], values: unique };
        setPiiKeyRows(nextRows);
        valuesDraftRef.current[index] = '';
    };

    const handleValuesSearch = (text, index) => {
        valuesDraftRef.current[index] = text;
    };

    const handleValuesInputKeyDown = (event, index) => {
        if (event.key !== 'Enter') {
            return;
        }
        const typed = String(event.target.value || '').trim();
        const existing = piiKeyRows[index].values || [];
        if (typed && existing.includes(typed)) {
            event.preventDefault();
            event.stopPropagation();
            valuesDraftRef.current[index] = '';
        }
    };

    const addKeyField = () => {
        setPiiKeyRows([...piiKeyRows, emptyRow()]);
    };

    const removeKeyField = index => {
        const nextRows = [...piiKeyRows];
        nextRows.splice(index, 1);
        setPiiKeyRows(nextRows.length ? nextRows : [emptyRow()]);
    };

    const handleSave = () => {
        const normalizedRows = [];
        const seenKeys = new Set();

        for (const row of piiKeyRows) {
            const key = (row.key || '').trim();
            if (!key) {
                continue;
            }
            if (seenKeys.has(key)) {
                message.error(`Duplicate PII key: ${key}`);
                return;
            }
            seenKeys.add(key);
            normalizedRows.push({
                key,
                values: Array.from(
                    new Set((row.values || []).map(value => String(value).trim()).filter(Boolean))
                ),
            });
        }

        updatePIIKeys({
            companyId,
            PIIKeyValues: normalizedRows,
        });
    };

    const isOnlyEmptyRow = piiKeyRows.length === 1 && !piiKeyRows[0].key && !piiKeyRows[0].values?.length;
    const hasValuesWithoutKey = piiKeyRows.some(
        row =>
            !(row.key || '').trim() &&
            (row.values || []).some(value => String(value).trim())
    );
    const isDirty = serializeRows(piiKeyRows) !== savedSnapshot;
    const canSave = isDirty && !hasValuesWithoutKey;

    const fieldRowStyle = {
        display: 'grid',
        gridTemplateColumns: 'minmax(160px, 1fr) minmax(220px, 2fr) 32px',
        columnGap: 8,
        alignItems: 'center',
    };

    return (
        <Modal
            title={`Manage PII Keys${companyName ? ` - ${companyName}` : ''}`}
            open={open}
            onCancel={onClose}
            onOk={handleSave}
            okText="Save"
            cancelText="Cancel"
            confirmLoading={isSaving}
            okButtonProps={{ disabled: !canSave }}
            destroyOnClose
            width={720}
        >
            <Spin spinning={isLoading}>
                <div style={{ ...fieldRowStyle, marginBottom: 4 }}>
                    <span style={{ color: 'rgba(0, 0, 0, 0.65)' }}>PII Key</span>
                    <span
                        style={{
                            color: 'rgba(0, 0, 0, 0.65)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                        }}
                    >
                        Acceptable Values
                        <Tooltip
                            title={
                                <span>
                                    To add several values:
                                    <br />
                                    1. Type one value (for example S)
                                    <br />
                                    2. Press Enter — it becomes a chip
                                    <br />
                                    3. Type the next value and press Enter again
                                    <br />
                                    Click a chip's x to remove it.
                                </span>
                            }
                        >
                            <InfoCircleOutlined
                                style={{ cursor: 'pointer', color: 'rgba(0, 0, 0, 0.45)' }}
                                aria-label="How to add multiple values"
                            />
                        </Tooltip>
                    </span>
                    <span />
                </div>
                {piiKeyRows.map((row, index) => (
                    <div key={index} style={{ ...fieldRowStyle, marginBottom: 8, alignItems: 'flex-start' }}>
                        <Input
                            placeholder="Enter PII key"
                            value={row.key}
                            onChange={e => handleKeyChange(e.target.value, index)}
                        />
                        <Select
                            mode="tags"
                            placeholder="e.g. value1 then press Enter"
                            value={row.values}
                            onChange={values => handleValuesChange(values, index)}
                            onSearch={text => handleValuesSearch(text, index)}
                            onInputKeyDown={event => handleValuesInputKeyDown(event, index)}
                            style={{ width: '100%' }}
                        />
                        <Button
                            icon={<DeleteOutlined />}
                            onClick={() => removeKeyField(index)}
                            disabled={isOnlyEmptyRow}
                        />
                    </div>
                ))}
                <Button type="dashed" onClick={addKeyField} icon={<PlusOutlined />} block>
                    Add PII Key
                </Button>
            </Spin>
        </Modal>
    );
};

export default ManagePIIKeysModal;
